import { Link } from "react-router-dom";
import { ArrowRight, DollarSign, Clock, Users, TrendingUp, Star, MessageCircle, CheckCircle } from "lucide-react";

const WHATSAPP_NUMBER = "19146730325";

const benefits = [
  {
    icon: DollarSign,
    title: "50% Venta Directa",
    desc: "Gana el 50% de comisión en cada producto que vendas directamente a tus clientes.",
  },
  {
    icon: TrendingUp,
    title: "50% Residual",
    desc: "Genera ingresos residuales del 50% por las compras recurrentes de tu red.",
  },
  {
    icon: Users,
    title: "50% Reclutamiento",
    desc: "Obtén el 50% por cada nuevo distribuidor que se una a tu equipo.",
  },
  {
    icon: Clock,
    title: "Maneja tu Tiempo",
    desc: "Trabaja desde cualquier lugar y a tu propio ritmo. Tú decides cuándo y cómo.",
  },
];

const steps = [
  { num: "01", title: "Contáctame", desc: "Escríbeme por WhatsApp o llena el formulario para recibir toda la información." },
  { num: "02", title: "Regístrate", desc: "Te guío paso a paso en el proceso de registro como distribuidor independiente." },
  { num: "03", title: "Capacítate", desc: "Recibe entrenamiento completo sobre los productos y estrategias de venta." },
  { num: "04", title: "Empieza a Ganar", desc: "Comienza a vender, reclutar y generar ingresos desde el primer día." },
];

const reasons = [
  "Presencia en más de 58 países",
  "Productos 100% naturales con respaldo científico",
  "Plan de compensación líder en la industria",
  "Sin inventario obligatorio",
  "Capacitación y soporte continuo",
  "Comunidad de emprendedores a nivel mundial",
];

export default function OpportunityPage() {
  const whatsappMsg = encodeURIComponent("Hola Oymer, me interesa la oportunidad de negocio como distribuidor de Vida Divina. ¿Puedes darme más información?");

  return (
    <div data-testid="opportunity-page">
      {/* Hero */}
      <section className="py-24 md:py-32 lg:py-40 hero-pattern relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-terracotta/10 rounded-full px-4 py-2">
                <Star size={14} className="text-terracotta" />
                <span className="font-body text-xs font-semibold tracking-[0.15em] uppercase text-terracotta">Oportunidad de Negocio</span>
              </div>
              <h1 className="font-heading text-5xl md:text-7xl tracking-tighter font-medium text-forest leading-[0.95]">
                Empieza tu<br />
                <span className="text-terracotta">propio negocio</span>
              </h1>
              <p className="font-body text-base md:text-lg leading-relaxed text-sage max-w-lg">
                Únete a Vida Divina como distribuidor independiente y gana hasta el <strong className="text-forest">50% de comisión</strong> en ventas directas, ingresos residuales y reclutamiento. Maneja tu tiempo como lo desees.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="opportunity-whatsapp-cta"
                  className="bg-[#25D366] text-white rounded-full px-8 py-4 font-body font-medium flex items-center gap-2 transition-transform duration-300 hover:scale-105 shadow-[0_8px_20px_rgba(37,211,102,0.25)]"
                >
                  <MessageCircle size={20} strokeWidth={1.5} /> Quiero más info
                </a>
                <Link
                  to="/contacto"
                  data-testid="opportunity-contact-cta"
                  className="bg-forest text-white rounded-full px-8 py-4 font-body font-medium transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_10px_20px_rgba(42,74,60,0.15)] inline-flex items-center gap-2"
                >
                  Contactar <ArrowRight size={18} />
                </Link>
              </div>
            </div>

            <div className="space-y-4">
              <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(42,74,60,0.12)]">
                <img
                  src="https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/110r2pxv_50000.jpg"
                  alt="Empieza tu negocio con Vida Divina"
                  className="w-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comisiones */}
      <section data-testid="commissions-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Ganancias</span>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest mt-2">
              Gana el 50% en tres formas
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((b, i) => (
              <div
                key={b.title}
                data-testid={`commission-card-${i}`}
                className="bg-sand rounded-[2rem] p-8 border border-[#E5E0D8] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgb(42,74,60,0.08)] opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.12}s`, animationFillMode: 'forwards' }}
              >
                <div className="w-14 h-14 rounded-2xl bg-forest/10 flex items-center justify-center mb-5">
                  <b.icon size={26} className="text-forest" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-forest mb-2">{b.title}</h3>
                <p className="font-body text-sm text-sage leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Imagen promocional */}
      <section className="py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_60px_rgba(42,74,60,0.12)]">
            <img
              src="https://customer-assets.emergentagent.com/job_vida-divina-shop/artifacts/41bbeyuz_49997.jpg"
              alt="Buscamos Distribuidores - Gana el 50% en venta directa"
              className="w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Por qué Vida Divina */}
      <section data-testid="why-section" className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="space-y-6">
              <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Ventajas</span>
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest">
                ¿Por qué Vida Divina?
              </h2>
              <p className="font-body text-base md:text-lg leading-relaxed text-sage">
                Vida Divina es una empresa líder en salud y bienestar con presencia global. Ofrece uno de los planes de compensación más generosos de la industria.
              </p>
              <ul className="space-y-3">
                {reasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 font-body text-sm text-sage">
                    <CheckCircle size={18} className="text-forest flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    {r}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-forest rounded-[2rem] p-10 md:p-12 text-white space-y-6">
              <h3 className="font-heading text-3xl font-semibold">Buscamos Distribuidores</h3>
              <p className="font-body text-white/70 leading-relaxed">
                No importa si no tienes experiencia. Te brindamos toda la capacitación y herramientas necesarias para que tengas éxito desde el primer día.
              </p>
              <div className="grid grid-cols-3 gap-4 py-4">
                <div className="text-center">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-terracotta-light">50%</p>
                  <p className="font-body text-xs text-white/60 mt-1">Venta Directa</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-terracotta-light">50%</p>
                  <p className="font-body text-xs text-white/60 mt-1">Residual</p>
                </div>
                <div className="text-center">
                  <p className="font-heading text-3xl md:text-4xl font-bold text-terracotta-light">50%</p>
                  <p className="font-body text-xs text-white/60 mt-1">Reclutamiento</p>
                </div>
              </div>
              <a
                href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="why-section-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white rounded-full px-6 py-3 font-body font-medium text-sm transition-transform duration-300 hover:scale-105"
              >
                <MessageCircle size={18} /> Únete ahora
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pasos */}
      <section data-testid="steps-section" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <span className="font-body text-xs font-semibold tracking-[0.2em] uppercase text-terracotta">Cómo Empezar</span>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-forest mt-2">
              4 pasos para iniciar
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((s, i) => (
              <div
                key={s.num}
                data-testid={`step-card-${i}`}
                className="relative opacity-0 animate-fade-in-up"
                style={{ animationDelay: `${i * 0.15}s`, animationFillMode: 'forwards' }}
              >
                <span className="font-heading text-6xl font-bold text-forest/5">{s.num}</span>
                <h3 className="font-heading text-lg font-semibold text-forest -mt-3">{s.title}</h3>
                <p className="font-body text-sm text-sage leading-relaxed mt-2">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 md:py-28 bg-forest">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center space-y-6">
          <h2 className="font-heading text-4xl md:text-5xl tracking-tight font-medium text-white">
            ¿Listo para cambiar tu vida?
          </h2>
          <p className="font-body text-base md:text-lg text-white/60 leading-relaxed">
            Contáctame hoy y te ayudo a dar el primer paso hacia tu independencia financiera con Vida Divina.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-4">
            <a
              href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMsg}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="final-cta-whatsapp"
              className="bg-[#25D366] text-white rounded-full px-8 py-4 font-body font-medium flex items-center gap-2 transition-transform duration-300 hover:scale-105 shadow-[0_8px_20px_rgba(37,211,102,0.25)]"
            >
              <MessageCircle size={20} strokeWidth={1.5} /> Escríbeme por WhatsApp
            </a>
            <Link
              to="/contacto"
              className="bg-white/10 text-white rounded-full px-8 py-4 font-body font-medium border border-white/20 transition-all duration-300 hover:-translate-y-1 hover:bg-white/20"
            >
              Formulario de Contacto
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
